import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Quote, Expand, X, Instagram, Globe } from "lucide-react";
import { XIcon, TikTokIcon, YouTubeIcon, FacebookIcon } from "@/components/SocialIcons";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, SectionBadge } from "@/components/Reveal";
import Logo from "@/components/Logo";
import { useLang } from "@/lib/i18n";

const GALLERY_SRCS = ["/screenshots/sendexpro-features.webp", "/screenshots/sendexpro-flow.webp"];

const SENDEXPRO_SOCIALS = [
  { icon: Globe, label: "sendexpro.com", testId: "website", href: "https://sendexpro.com/" },
  { icon: YouTubeIcon, label: "SendexPro on YouTube", testId: "youtube", href: "https://www.youtube.com/@Sendexpro" },
  { icon: TikTokIcon, label: "SendexPro on TikTok", testId: "tiktok", href: "https://www.tiktok.com/@sendexproo" },
  { icon: Instagram, label: "SendexPro on Instagram", testId: "instagram", href: "https://www.instagram.com/sendexpro/" },
  { icon: XIcon, label: "SendexPro on X", testId: "x", href: "https://x.com/Sendexpro" },
  { icon: FacebookIcon, label: "SendexPro on Facebook", testId: "facebook", href: "https://www.facebook.com/SendexPro/" },
];

function SendexProSocials({ idPrefix, centered = true }) {
  const { t } = useLang();
  return (
    <div className={`flex flex-col gap-4 ${centered ? "items-center" : "items-start"}`}>
      <span className="font-tech text-[9px] uppercase tracking-[0.2em] text-[#686D76]">
        {t("case.socialsLabel")}
      </span>
      <div className="flex items-center gap-3">
        {SENDEXPRO_SOCIALS.map((s) => (
          <a
            key={s.label}
            data-testid={`${idPrefix}-${s.testId}`}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={s.label}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#262930] bg-[#121418] text-[#9E9E9E] transition-colors duration-200 hover:border-[#4A6CF7] hover:text-[#4A6CF7]"
          >
            <s.icon className="h-4 w-4" />
          </a>
        ))}
      </div>
    </div>
  );
}

export default function SendexPro() {
  const navigate = useNavigate();
  const [zoom, setZoom] = useState(null);
  const { t } = useLang();
  const meta = t("case.meta");
  const chapters = t("case.chapters");
  const gallery = t("case.gallery").map((g, i) => ({ ...g, src: GALLERY_SRCS[i] }));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goContact = () => {
    navigate("/", { state: { scrollTo: "contact" } });
  };

  return (
    <div data-testid="case-study-page" className="relative min-h-screen bg-[#0B0C0E] text-[#EAE6E1]">
      <div className="noise-overlay" aria-hidden="true" />

      <header className="fixed top-0 left-0 right-0 z-50 border-b border-[#262930] bg-[#0B0C0E]/80 backdrop-blur-xl pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="case-study-logo" aria-label="Multigroupnetwork">
            <Logo />
          </Link>
          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to="/"
              data-testid="case-study-back-link"
              className="hidden sm:flex items-center gap-1.5 text-sm text-[#9E9E9E] transition-colors duration-200 hover:text-[#EAE6E1]"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("case.back")}
            </Link>
            <a
              href="https://sendexpro.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="case-study-live-link"
              className="group flex items-center gap-1.5 rounded-full bg-[#8C9A86] px-4 py-2 text-xs font-semibold text-[#0B0C0E] transition-colors duration-200 hover:bg-[#9FAF98] md:px-5 md:text-sm"
            >
              sendexpro.com
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="hero-grid-bg pt-24 md:pt-40 pb-10 md:pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <SectionBadge label={t("case.badge")} testId="case-study-badge" />
              <h1
                data-testid="case-study-title"
                className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-[#EAE6E1]"
              >
                SendexPro
              </h1>
              <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-[#9E9E9E]">
                {t("case.desc")}
              </p>
              <div className="mt-8">
                <SendexProSocials idPrefix="sendexpro-social-top" centered={false} />
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div data-testid="case-study-meta" className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 border-y border-[#262930] py-4 md:py-6">
                {meta.map((m) => (
                  <div key={m.label}>
                    <div className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#686D76]">{m.label}</div>
                    <div className="mt-1.5 text-sm font-semibold text-[#EAE6E1]">{m.value}</div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-8 md:mt-12 overflow-hidden rounded-2xl border border-[#262930]">
              <div className="flex h-44 md:h-[420px] w-full items-center justify-center bg-white p-6 md:p-10">
                <img
                  data-testid="case-study-hero-image"
                  src="/sendexpro-logo.webp"
                  alt={t("case.heroImgAlt")}
                  className="max-h-full w-auto max-w-full object-contain"
                />
              </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-12 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <SectionBadge label={t("case.storyBadge")} testId="case-study-story-badge" />
              <h2 className="font-display mt-5 max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-[#EAE6E1]">
                {t("case.storyHeading")}
              </h2>
            </Reveal>
            <div className="mt-10 md:mt-14 grid grid-cols-2 gap-3 md:gap-6">
              {chapters.map((ch, i) => (
                <Reveal key={ch.num} delay={i * 0.08}>
                  <div
                    data-testid={`case-study-chapter-${i}`}
                    className="group h-full rounded-xl border border-[#262930] bg-[#121418] p-3 md:p-7 transition-colors duration-200 hover:border-[#3A3F4B] hover:bg-[#181A20]"
                  >
                    <div className="font-tech text-[7px] md:text-[10px] uppercase tracking-[0.12em] md:tracking-[0.2em] text-[#C87A5B]">
                      {t("case.chapterLabel")} {ch.num}
                    </div>
                    <h3 className="font-display mt-2 md:mt-4 text-sm md:text-xl font-semibold tracking-tight text-[#EAE6E1]">{ch.title}</h3>
                    <p className="mt-2 md:mt-3 text-[10px] md:text-sm leading-relaxed text-[#9E9E9E]">{ch.body}</p>
                    <div className="mt-3 md:mt-6 h-px w-full bg-[#262930] transition-colors duration-200 group-hover:bg-[#8C9A86]" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-[#262930] bg-[#0E1013] py-12 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <SectionBadge label={t("case.galleryBadge")} testId="case-study-gallery-badge" />
              <h2 className="font-display mt-5 max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-[#EAE6E1]">
                {t("case.galleryHeading")}
              </h2>
            </Reveal>
            <div data-testid="case-study-gallery" className="mt-10 md:mt-14 grid grid-cols-2 gap-3 md:gap-6">
              {gallery.map((g, i) => (
                <Reveal key={g.caption} delay={i * 0.1}>
                  <figure
                    data-testid={`gallery-item-${i}`}
                    onClick={() => setZoom(g)}
                    className="group cursor-zoom-in overflow-hidden rounded-2xl border border-[#262930] bg-[#121418]"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={g.src}
                        alt={g.caption}
                        loading="lazy"
                        className="h-36 md:h-80 w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                      <span className="absolute right-2 top-2 md:right-3 md:top-3 flex h-7 w-7 items-center justify-center rounded-full bg-[#0B0C0E]/70 text-[#EAE6E1] opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100">
                        <Expand className="h-3.5 w-3.5" />
                      </span>
                    </div>
                    <figcaption className="border-t border-[#262930] px-2.5 py-2 md:px-6 md:py-4 font-tech text-[8px] md:text-[10px] uppercase tracking-[0.1em] md:tracking-[0.15em] leading-relaxed text-[#686D76]">
                      {g.caption}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <figure
                data-testid="case-study-quote"
                className="mt-10 md:mt-14 rounded-2xl border border-[#262930] bg-[#121418] p-5 md:p-10"
              >
                <Quote className="h-6 w-6 text-[#8C9A86]" />
                <blockquote className="mt-4 md:mt-5 max-w-3xl font-display text-base md:text-2xl font-semibold leading-relaxed tracking-tight text-[#EAE6E1]">
                  {t("case.quote")}
                </blockquote>
                <figcaption className="mt-6 border-t border-[#262930] pt-5">
                  <span className="font-tech text-[10px] uppercase tracking-[0.15em] text-[#C87A5B]">
                    {t("case.quoteBy")}
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </section>

        <section className="py-12 md:py-28">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <SectionBadge label={t("case.ctaBadge")} testId="case-study-cta-badge" />
              <h2 className="font-display mt-5 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-[#EAE6E1]">
                {t("case.ctaHeading")}
              </h2>
              <p className="mt-4 text-base text-[#9E9E9E] leading-relaxed">
                {t("case.ctaBody")}
              </p>
              <div className="mt-8 md:mt-10 flex flex-nowrap items-center justify-center gap-3 md:gap-4">
                <button
                  data-testid="case-study-cta-start"
                  onClick={goContact}
                  className="group flex items-center gap-2 whitespace-nowrap rounded-full bg-[#8C9A86] px-4 py-3 text-[11px] font-semibold text-[#0B0C0E] transition-colors duration-200 hover:bg-[#9FAF98] md:px-7 md:py-3.5 md:text-sm"
                >
                  {t("case.ctaStart")}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <a
                  href="https://sendexpro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="case-study-cta-visit"
                  className="group flex items-center gap-2 whitespace-nowrap rounded-full border border-[#3A3F4B] px-4 py-3 text-[11px] font-semibold text-[#EAE6E1] transition-colors duration-200 hover:border-[#8C9A86] hover:text-[#8C9A86] md:px-7 md:py-3.5 md:text-sm"
                >
                  {t("case.ctaVisit")}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>

              <div className="mt-10">
                <SendexProSocials idPrefix="sendexpro-social" />
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#262930] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#686D76]">
            {t("case.footerCase")}
          </span>
          <Link
            to="/"
            data-testid="case-study-footer-home"
            className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#8C9A86] transition-colors duration-200 hover:text-[#9FAF98]"
          >
            {t("case.footerHome")}
          </Link>
        </div>
      </footer>

      <AnimatePresence>
        {zoom && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setZoom(null)}
            className="fixed inset-0 z-[110] flex flex-col items-center justify-center bg-[#0B0C0E]/95 p-4 backdrop-blur-sm"
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setZoom(null)}
              aria-label={t("case.closeAria")}
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-[#3A3F4B] bg-[#121418] text-[#EAE6E1] transition-colors duration-200 hover:border-[#8C9A86] hover:text-[#8C9A86]"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.img
              src={zoom.src}
              alt={zoom.caption}
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 22 }}
              className="max-h-[80vh] max-w-[92vw] rounded-xl border border-[#262930] object-contain"
            />
            <p className="mt-4 font-tech text-[10px] uppercase tracking-[0.2em] text-[#9E9E9E]">
              {zoom.caption}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
